import { useState } from 'react';
import { Move, GymnastFriend, UserProgress, ProgressionStep } from '../../types';
import GymnastAvatar from '../GymnastAvatar';
import { Star, CheckCircle2, ShieldAlert, Sparkles, X, RotateCcw } from 'lucide-react';
import { fireStepConfetti, fireMasteryConfetti } from '../../utils/confetti';

interface VariantProps {
  moves: Move[];
  friends: GymnastFriend[];
  progress: UserProgress;
  onToggleStep: (stepId: string, move: Move) => { nowCompleted: boolean; moveJustMastered: boolean };
  onSelectFriend: (friendId: string) => void;
  onResetProgress: () => void;
}

export default function VariantB({
  moves,
  friends,
  progress,
  onToggleStep,
  onResetProgress,
}: VariantProps) {
  const [selectedMoveId, setSelectedMoveId] = useState<string>(moves[0]?.id || 'move-backbend');
  const [activeStepModal, setActiveStepModal] = useState<ProgressionStep | null>(null);

  const activeMove = moves.find(m => m.id === selectedMoveId) || moves[0];
  const activeFriend = friends.find(f => f.id === activeMove.coachFriendId) || friends[0];
  const steps = activeMove.steps;

  const handleToggle = (step: ProgressionStep) => {
    const res = onToggleStep(step.id, activeMove);
    if (res.moveJustMastered) {
      fireMasteryConfetti();
    } else if (res.nowCompleted) {
      fireStepConfetti();
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
      {/* Header */}
      <header className="flex items-center justify-between bg-white/90 backdrop-blur-md rounded-3xl p-4 md:p-6 shadow-md border-2 border-emerald-200 mb-6">
        <div className="flex items-center gap-3">
          <div className="text-3xl">🗺️</div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-emerald-700">
              Gymnastics Adventure Map
            </h1>
            <p className="text-xs md:text-sm font-semibold text-slate-600">
              Hop along the stepping stones to master {activeMove.name}!
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-yellow-100 border-2 border-yellow-300 px-3.5 py-1.5 rounded-full">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-400 animate-pulse" />
            <span className="text-lg font-black text-yellow-800">{progress.earnedStars}</span>
          </div>
          <button
            onClick={onResetProgress}
            title="Reset progress"
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Move Tabs */}
      <nav aria-label="Gymnastics moves" className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 scrollbar-none">
        {moves.map(m => (
          <button
            key={m.id}
            onClick={() => {
              setSelectedMoveId(m.id);
              setActiveStepModal(null);
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-bold text-sm whitespace-nowrap transition-all shadow-sm ${
              m.id === activeMove.id
                ? 'bg-emerald-600 text-white scale-105 ring-2 ring-emerald-300'
                : 'bg-white/80 text-slate-700 hover:bg-white'
            }`}
          >
            <span>{m.badgeEmoji}</span>
            <span>{m.name}</span>
          </button>
        ))}
      </nav>

      {/* Adventure Path Canvas */}
      <main className="bg-gradient-to-b from-sky-100 via-emerald-50 to-amber-50 rounded-3xl p-6 md:p-10 shadow-xl border-4 border-emerald-200 relative">
        {/* Coach Mascot Calling Out */}
        <div className="flex items-center gap-4 bg-white/90 rounded-2xl p-4 shadow-sm border border-emerald-200 mb-8">
          <GymnastAvatar friend={activeFriend} size="sm" pose="cheer" />
          <div>
            <div className="text-xs font-bold uppercase text-emerald-600">
              Your Guide: {activeFriend.name} ({activeFriend.title})
            </div>
            <p className="text-sm font-medium text-slate-700">
              "{activeFriend.tagline} Tap each stone to start your training!"
            </p>
          </div>
        </div>

        {/* Vertical Winding Stepping Stones */}
        <div className="flex flex-col items-center gap-6 relative py-4">
          {steps.map((step, idx) => {
            const isCompleted = !!progress.completedStepIds[step.id];
            // Alternating zigzag offset
            const offsetClass = idx % 2 === 0 ? '-translate-x-6 md:-translate-x-12' : 'translate-x-6 md:translate-x-12';

            return (
              <div key={step.id} className={`flex items-center gap-4 transition-transform ${offsetClass}`}>
                <button
                  onClick={() => setActiveStepModal(step)}
                  className={`group relative flex flex-col items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-3xl font-black transition-all transform hover:scale-110 active:scale-95 shadow-lg ${
                    isCompleted
                      ? 'bg-gradient-to-br from-yellow-300 to-amber-400 text-amber-900 border-4 border-yellow-200 ring-4 ring-yellow-200/50'
                      : 'bg-white text-emerald-800 border-4 border-emerald-300 hover:border-emerald-400'
                  }`}
                >
                  <span className="text-xl md:text-2xl">
                    {isCompleted ? '⭐' : `0${idx + 1}`}
                  </span>
                  <span className="text-[10px] md:text-xs font-bold uppercase tracking-tight line-clamp-1 px-1">
                    {step.title.split(' ')[0]}
                  </span>

                  {isCompleted && (
                    <div className="absolute -top-2 -right-2 bg-emerald-500 text-white rounded-full p-1 shadow">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                  )}
                </button>

                <div className="bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-xl border border-emerald-100 shadow-sm text-xs font-bold text-slate-700 max-w-[140px] md:max-w-[200px]">
                  {step.title}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Step Modal / Drawer */}
      {activeStepModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl border-4 border-emerald-300 relative animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setActiveStepModal(null)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <span className="bg-emerald-600 text-white text-xs font-black px-3 py-1 rounded-full uppercase">
                Step {activeStepModal.number}
              </span>
              <h2 className="text-xl font-bold text-slate-800">{activeStepModal.title}</h2>
            </div>

            <p className="text-slate-700 font-medium mb-4">{activeStepModal.description}</p>

            <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200 mb-4">
              <h3 className="text-xs uppercase font-extrabold text-emerald-700 mb-2">Instructions</h3>
              <ul className="space-y-1.5 text-xs md:text-sm text-slate-700 font-medium">
                {activeStepModal.instructions.map((inst, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-emerald-600 font-bold">•</span>
                    <span>{inst}</span>
                  </li>
                ))}
              </ul>
            </div>

            {activeStepModal.safetyTip && (
              <div className="flex items-start gap-2 bg-amber-50 p-3 rounded-xl border border-amber-200 text-amber-800 text-xs mb-6">
                <ShieldAlert className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span>{activeStepModal.safetyTip}</span>
              </div>
            )}

            <button
              onClick={() => {
                handleToggle(activeStepModal);
              }}
              className={`w-full py-3.5 rounded-2xl font-black text-base flex items-center justify-center gap-2 shadow-lg transition-all ${
                progress.completedStepIds[activeStepModal.id]
                  ? 'bg-yellow-400 hover:bg-yellow-500 text-amber-900 shadow-yellow-200'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200'
              }`}
            >
              <Sparkles className="w-5 h-5" />
              <span>
                {progress.completedStepIds[activeStepModal.id]
                  ? 'Step Completed! ⭐ (Tap to Undo)'
                  : 'I Mastered This Stone! ⭐'}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
